/**
 * Generic helper utilities for state sanitization.
 *
 * These are pure functions with no domain knowledge — they validate
 * and coerce primitive types and merge object shapes against defaults.
 */

/**
 * Deep-merges `defaults` into `value`, ensuring every key in defaults
 * exists in the result with the correct type. Unknown keys in `value`
 * are preserved (they may be future fields added in newer code).
 */

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TODO
export function deepMergeDefaults<T extends Record<string, unknown>>(value: unknown, defaults: T): T {
  if (value === null || value === undefined || typeof value !== 'object' || Array.isArray(value)) {
    return { ...defaults };
  }

  const source = value as Record<string, unknown>;
  const result = { ...defaults };

  for (const key of Object.keys(defaults)) {
    const defaultVal = (defaults as Record<string, unknown>)[key];
    const sourceVal = source[key];

    if (sourceVal === undefined || sourceVal === null) {
      (result as Record<string, unknown>)[key] = defaultVal;
    } else if (
      typeof defaultVal === 'object' &&
      defaultVal !== null &&
      !Array.isArray(defaultVal) &&
      typeof sourceVal === 'object' &&
      sourceVal !== null &&
      !Array.isArray(sourceVal)
    ) {
      (result as Record<string, unknown>)[key] = deepMergeDefaults(sourceVal, defaultVal as Record<string, unknown>);
    } else if (typeof sourceVal !== typeof defaultVal && defaultVal !== undefined) {
      (result as Record<string, unknown>)[key] = defaultVal;
    } else {
      (result as Record<string, unknown>)[key] = sourceVal;
    }
  }

  for (const key of Object.keys(source)) {
    if (!(key in defaults)) {
      (result as Record<string, unknown>)[key] = source[key];
    }
  }

  return result;
}

/** Ensures `value` is an array. Returns `fallback` when it is not. */
export function ensureArray<T>(value: unknown, fallback: T[] = []): T[] {
  return Array.isArray(value) ? value : fallback;
}

/** Ensures `value` is a number within bounds. Falls back to `defaultVal`. */
export function ensureNumber(value: unknown, defaultVal: number, min?: number, max?: number): number {
  const n = typeof value === 'number' && !Number.isNaN(value) ? value : defaultVal;
  if (min !== undefined && n < min) {
    return defaultVal;
  }
  if (max !== undefined && n > max) {
    return defaultVal;
  }
  return n;
}

/** Ensures `value` is a boolean. Falls back to `defaultVal`. */
export function ensureBoolean(value: unknown, defaultVal: boolean): boolean {
  return typeof value === 'boolean' ? value : defaultVal;
}

/** Ensures `value` is a string. Falls back to `defaultVal`. */
export function ensureString(value: unknown, defaultVal: string): string {
  return typeof value === 'string' ? value : defaultVal;
}

/** Parses a value into a Date, returns null if invalid. */
export function parseDate(value: unknown): Date | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
      return d;
    }
  }
  return null;
}
